import React, { useState, useEffect } from 'react';
import {
  PowerService,
  InstalledCapacityResponse,
  TimeGranularity,
  DataType,
} from '../../../services/powerService';
import { supabase } from '../../auth/AuthProvider';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { format } from 'date-fns';
import InstalledCapacityChart from './InstalledCapacityChart';
import InstalledCapacitySummary from './InstalledCapacitySummary';

interface InstalledCapacityProps {
  defaultCountryCode?: string;
}

const dataTypes: { value: DataType; label: string }[] = [
  { value: 'total', label: 'Total Capacity' },
  { value: 'delta', label: 'Capacity Changes' },
];

const InstalledCapacity: React.FC<InstalledCapacityProps> = ({ defaultCountryCode = 'DE' }) => {
  const [countryOptions, setCountryOptions] = useState<Record<string, string> | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountryCode);
  const [selectedDataType, setSelectedDataType] = useState<DataType>('total');
  const [capacityData, setCapacityData] = useState<InstalledCapacityResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate time range - default to last 10 years for yearly
  const getTimeRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 10);

    return {
      start_time: format(startDate, 'yyyy-MM-dd'),
      end_time: format(endDate, 'yyyy-MM-dd'),
    };
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);
        const countriesResponse = await powerService.getInstalledCapacityCountries();

        if (countriesResponse.error || !countriesResponse.data) {
          throw new Error(countriesResponse.error || 'Failed to fetch countries');
        }

        setCountryOptions(countriesResponse.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchCapacityData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);
        const timeRange = getTimeRange();

        const params = {
          country_code: selectedCountry,
          granularity: 'yearly' as TimeGranularity,
          data_type: selectedDataType,
          start_time: timeRange.start_time,
          end_time: timeRange.end_time,
        };

        const dataResponse = await powerService.getInstalledCapacity(params);

        if (dataResponse.error || !dataResponse.data) {
          throw new Error(dataResponse.error || 'Failed to fetch installed capacity data');
        }

        setCapacityData(dataResponse.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCapacityData();
  }, [selectedCountry, selectedDataType]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };

  const handleDataTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDataType(e.target.value as DataType);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Installed Power Capacity
        </h2>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Country
            </label>
            <select
              id="country"
              value={selectedCountry}
              onChange={handleCountryChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {countryOptions &&
                Object.entries(countryOptions).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              {!countryOptions && <option value={defaultCountryCode}>{defaultCountryCode}</option>}
            </select>
          </div>

          <div>
            <label
              htmlFor="dataType"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Data Type
            </label>
            <select
              id="dataType"
              value={selectedDataType}
              onChange={handleDataTypeChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {dataTypes.map((dataType) => (
                <option key={dataType.value} value={dataType.value}>
                  {dataType.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md text-red-700 dark:text-red-400">
            <p>Error: {error}</p>
          </div>
        ) : capacityData && capacityData.data.length > 0 ? (
          <div className="space-y-6">
            <InstalledCapacitySummary capacityData={capacityData} dataType={selectedDataType} />
            <InstalledCapacityChart capacityData={capacityData} dataType={selectedDataType} />
          </div>
        ) : (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md text-yellow-700 dark:text-yellow-400">
            <p>No data available for the selected filters. Please try a different combination.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstalledCapacity;
